import React, { useEffect, useState } from 'react';
import { Button, Modal, notification, Spin } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { signMinvoice } from 'services/api/module/booking.api';
import { useTranslation } from 'react-i18next';
import { queryClient } from 'index';
import QUERY_KEYS from 'services/api/queryKeys';
import { LoadingDots } from 'utils/loadingDots';

interface InvoicePreviewModalProps {
  isOpen: boolean;
  previewUrl?: string;
  guestOrderId?: number;
  currentOrder: any;
  onClose: () => void;
  setKeyDate?: () => void;
}

function InvoicePreviewModal({
  isOpen,
  previewUrl,
  guestOrderId,
  currentOrder,
  onClose,
  setKeyDate
}: InvoicePreviewModalProps) {
  const { t } = useTranslation();
  const [isSigned, setIsSigned] = useState<boolean>(false);
  const [isIframeLoading, setIsIframeLoading] = useState<boolean>(true);
  const [loadingError, setLoadingError] = useState<boolean>(false);

  const { mutateAsync: mutateSignMinvoice } = useMutation({
    mutationFn: (params: any) => signMinvoice(params.guest_order_id)
  });

  const handleSignMinvoice = async () => {
    if (!guestOrderId) return;

    try {
      await mutateSignMinvoice({ guest_order_id: guestOrderId });
      notification.success({
        message: t('common.message.signMinvoiceSuccessfully')
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_GUEST_TRANSACTIONS, currentOrder.booking_line_guest_id]
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_MINVOICE_PREVIEW, guestOrderId]
      });

      if (setKeyDate) {
        setKeyDate();
      }
    } catch (error: any) {
      notification.error({
        message: error.error || t('common.error')
      });
    }
  };

  useEffect(() => {
    setIsSigned(currentOrder?.einvoice_signed);
  }, [currentOrder]);

  // Handle iframe load completion
  const handleIframeLoad = () => {
    setIsIframeLoading(false);
    setLoadingError(false);
  };

  // Handle iframe loading error
  const handleIframeError = () => {
    setIsIframeLoading(false);
    setLoadingError(true);
  };

  // Setup timeout for iframe loading
  useEffect(() => {
    if (previewUrl && isIframeLoading && !loadingError) {
      const timeoutId = setTimeout(() => {
        if (isIframeLoading) {
          setIsIframeLoading(false);
          setLoadingError(true);
        }
      }, 18000);

      return () => clearTimeout(timeoutId);
    }
  }, [previewUrl, isIframeLoading, loadingError]);

  // Reset states when modal opens/closes or previewUrl changes
  useEffect(() => {
    if (isOpen && previewUrl) {
      setIsIframeLoading(true);
      setLoadingError(false);
    }
  }, [isOpen, previewUrl]);

  return (
    <Modal
      title={t('cashierPage.previewMinvoicePage.title')}
      centered
      width={1200}
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button
          key="sign"
          onClick={handleSignMinvoice}
          className="ant-btn-secondary"
          disabled={isSigned || isIframeLoading || !previewUrl || loadingError}
          loading={isIframeLoading}
        >
          {t('cashierPage.previewMinvoicePage.sign')}
        </Button>,
        <Button key="close" type="default" onClick={onClose} disabled={isIframeLoading}>
          {t('cashierPage.invoice.cancel')}
        </Button>
      ]}
    >
      <div style={{ position: 'relative', minHeight: '700px', width: '100%' }}>
        <Spin
          spinning={isIframeLoading}
          indicator={<LoadingDots />}
          size="large"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10
          }}
        >
          <div style={{ minHeight: '700px' }}>
            {previewUrl && !loadingError ? (
              <iframe
                src={previewUrl}
                width="100%"
                height="700px"
                title={t('cashierPage.previewMinvoicePage.title')}
                style={{ border: 'none' }}
                onLoad={handleIframeLoad}
                onError={handleIframeError}
              />
            ) : (
              (!previewUrl || loadingError) && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center',
                    padding: '20px'
                  }}
                >
                  <p style={{ color: 'red', fontSize: '16px', whiteSpace: 'pre-line' }}>
                    {t('cashierPage.previewMinvoicePage.loadingFailed')}
                  </p>
                </div>
              )
            )}
          </div>
        </Spin>
      </div>
    </Modal>
  );
}

export default InvoicePreviewModal;
